import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUpdateOrganizationSettings } from '@modules-deprecated/app/organization/hooks/useUpdateOrganizationSettings'
import type { OrganizationSettingsValue } from '@modules-deprecated/app/organization/types/organizationSettingsValue'

import { EmberRoute } from '../../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useEmberTransitionTo } from '../../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { ReconciliationViewBannerPage } from '../../enums/reconciliationViewBannerPage'
import { useShouldShowReconciliationViewBanner } from '../../hooks/useShouldShowReconciliationViewBanner'
import { ReconciliationViewBannerDismissDialog } from '../ReconciliationViewBannerDismissDialog/ReconciliationViewBannerDismissDialog'
import { ReconciliationViewCustomBanner } from '../ReconciliationViewCustomBanner/ReconciliationViewCustomBanner'

interface ReconciliationViewBannerConfig {
  actionKey: string
  messageKey: string
  titleKey: string
  /** Value of `isLegacyReconciliationViewEnabled` that switches the organization to the other view */
  targetLegacyReconciliationViewValue: OrganizationSettingsValue
}

const reconciliationViewBannerConfig: Record<ReconciliationViewBannerPage, ReconciliationViewBannerConfig> = {
  [ReconciliationViewBannerPage.Reconciliation]: {
    actionKey: 'bank_transactions.reconciliation_view_banner.reconciliation.action',
    messageKey: 'bank_transactions.reconciliation_view_banner.reconciliation.message',
    titleKey: 'bank_transactions.reconciliation_view_banner.reconciliation.title',
    targetLegacyReconciliationViewValue: '0',
  },
  [ReconciliationViewBannerPage.Transactions]: {
    actionKey: 'bank_transactions.reconciliation_view_banner.transactions.action',
    messageKey: 'bank_transactions.reconciliation_view_banner.transactions.message',
    titleKey: 'bank_transactions.reconciliation_view_banner.transactions.title',
    targetLegacyReconciliationViewValue: '1',
  },
}

interface ReconciliationViewBannerProps {
  accountId?: string
  page: ReconciliationViewBannerPage
}

export const ReconciliationViewBanner = ({ accountId, page }: ReconciliationViewBannerProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { shouldShowBanner } = useShouldShowReconciliationViewBanner(page)
  const { update: updateOrganizationSettings } = useUpdateOrganizationSettings()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { transitionTo } = useEmberTransitionTo()
  const { url: bankTransactionsUrl } = useOrganizationViewUrl(
    reactRoute.organization.bank.getBankTransactionsRoute(),
    accountId ? { accountId } : undefined,
  )
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false)

  const isOnReconciliationPage = page === ReconciliationViewBannerPage.Reconciliation
  const { actionKey, messageKey, targetLegacyReconciliationViewValue, titleKey } = reconciliationViewBannerConfig[page]

  const handleSwitchView = useCallback(() => {
    if (!organization?.id) {
      return
    }

    const reconciliationUrl = accountId
      ? generateUrl?.(EmberRoute.Bank, accountId)
      : generateUrl?.(EmberRoute.BankAccounts)
    const targetUrl = isOnReconciliationPage ? bankTransactionsUrl : reconciliationUrl

    updateOrganizationSettings(
      {
        key: 'isLegacyReconciliationViewEnabled',
        organizationId: organization.id,
        value: targetLegacyReconciliationViewValue,
      },
      {
        onSuccess: () => {
          if (targetUrl) {
            transitionTo(targetUrl)
          }
        },
      },
    )
  }, [
    accountId,
    bankTransactionsUrl,
    generateUrl,
    isOnReconciliationPage,
    organization?.id,
    targetLegacyReconciliationViewValue,
    transitionTo,
    updateOrganizationSettings,
  ])

  const handleDismissClick = useCallback(() => {
    setIsDismissDialogOpen(true)
  }, [])

  const handleDismissDialogClose = useCallback(() => {
    setIsDismissDialogOpen(false)
  }, [])

  const handleDismissConfirm = useCallback(() => {
    setIsDismissDialogOpen(false)

    if (!organization?.id) {
      return
    }

    updateOrganizationSettings({
      key: 'isReconciliationViewBannerDismissed',
      organizationId: organization.id,
      value: '1',
    })
  }, [organization?.id, updateOrganizationSettings])

  if (!shouldShowBanner) {
    return null
  }

  return (
    <>
      <ReconciliationViewCustomBanner
        actionLabel={t(actionKey)}
        message={t(messageKey)}
        title={t(titleKey)}
        onAction={handleSwitchView}
        onDismiss={handleDismissClick}
      />
      <ReconciliationViewBannerDismissDialog
        isOpen={isDismissDialogOpen}
        onClose={handleDismissDialogClose}
        onConfirm={handleDismissConfirm}
      />
    </>
  )
}
